import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Spinner,
  Stack,
  Text,
} from '@chakra-ui/react';
import styled from '@emotion/styled';
import { zodResolver } from '@hookform/resolvers/zod';
import { AgreePoliciesCheckbox } from '@resistapp/client/components/agree-policies-checkbox/agree-policies-checkbox';
import { SpacingContainer } from '@resistapp/client/components/containers/spacing-container';
import { RSecondTitle } from '@resistapp/client/components/headers/r-second-title';
import { LocalStorageId } from '@resistapp/client/components/shared/local-storage';
import { oldCss, theme } from '@resistapp/client/components/shared/theme';
import { useUser } from '@resistapp/client/contexts/use-user-context';
import { useInviteLookup, useSignupInvite } from '@resistapp/client/hooks/api';
import { useNavigateWithQuery } from '@resistapp/client/hooks/use-navigate-with-query';
import { PathParams, usePathParam } from '@resistapp/client/hooks/use-path-params';
import { SignupInviteRequest, zSignupInviteRequest } from '@resistapp/common/api-types';
import { AxiosError } from 'axios';
import { Fragment, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import useLocalStorageState from 'use-local-storage-state';
import { SignupLogo } from '../../components/icons/signup-logo';

const formFields = [
  {
    name: 'password' as const,
    label: 'Password',
    type: 'password',
  },
  {
    name: 'firstName' as const,
    label: 'First name',
    type: 'text',
  },
  {
    name: 'lastName' as const,
    label: 'Last name',
    type: 'text',
  },
] satisfies Array<{ name: keyof SignupInviteRequest; label: string; type: string }>;

export function InvitedSignupView() {
  const inviteCode = usePathParam(PathParams.inviteCode);
  const { logout, user, setLoggedIn } = useUser();
  const { create } = useSignupInvite();
  const navigate = useNavigateWithQuery();
  const [cookiesAccepted, setCookiesAccepted] = useLocalStorageState(LocalStorageId.cookiesAccepted, {
    defaultValue: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
  } = useForm<SignupInviteRequest>({
    resolver: zodResolver(zSignupInviteRequest),
    defaultValues: useMemo(
      () => ({
        inviteCode: inviteCode ?? '',
      }),
      [inviteCode],
    ),
  });

  const inviteQuery = useInviteLookup(inviteCode);
  const inviteData = inviteQuery.data;

  if (user) {
    logout(inviteCode ? `/signup/invite/${inviteCode}` : '/signup');
    navigate(0, true);
  }

  if (!inviteCode) {
    return (
      <div>
        Please check your invitation link, or <a href="mailto:laboratory@resistomap.com">contact us</a> for assistance.
      </div>
    );
  }

  const onSubmit = async (values: SignupInviteRequest) => {
    try {
      const createdUser = await create.mutateAsync({
        ...values,
        inviteCode,
      });
      setLoggedIn(createdUser);
      navigate('/', false, true);
    } catch (caughtError: unknown) {
      if (caughtError instanceof AxiosError && [400, 403, 404].includes(caughtError.response?.status ?? 0)) {
        console.warn(caughtError.message);
      } else {
        console.error(caughtError);
      }
    }
  };

  const onValidationFailed = (_errors: unknown) => {
    console.warn('Form validation failed', _errors);
  };

  if (inviteQuery.isLoading) {
    return (
      <Flex direction="column" alignItems="center">
        <SpacingSignupContainer>
          <SignupLogo />
          <HStackLoading>
            <Spinner size="sm" />
            <Text>Loading invitation...</Text>
          </HStackLoading>
        </SpacingSignupContainer>
      </Flex>
    );
  }

  if (inviteQuery.error || !inviteData) {
    const inviteLookupErrorMessage =
      inviteQuery.error instanceof Error ? inviteQuery.error.message : 'Invitation not found or expired.';
    return (
      <Flex direction="column" alignItems="center">
        <SpacingSignupContainer>
          <SignupLogo />
          <Alert status="error" style={{ margin: '10px 0' }}>
            <AlertIcon />
            <AlertDescription>{inviteLookupErrorMessage}</AlertDescription>
          </Alert>
        </SpacingSignupContainer>
      </Flex>
    );
  }

  return (
    <Flex direction="column" alignItems="center">
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <SpacingSignupContainer as="form" onSubmit={handleSubmit(onSubmit, onValidationFailed)}>
        <SignupLogo />
        <RSecondTitle>Resistomap Invitation Signup</RSecondTitle>

        <Stack spacing={6}>
          <FormControl>
            <FormLabel>Email</FormLabel>
            <Input value={inviteData.email} isReadOnly />
          </FormControl>
          <FormControl>
            <FormLabel>Organisation</FormLabel>
            <Input value={inviteData.organisationName} isReadOnly />
          </FormControl>
          {formFields.map(field => (
            <Fragment key={field.name}>
              <FormControl isInvalid={!!errors[field.name]}>
                <FormLabel>{field.label}</FormLabel>
                <Input
                  placeholder={field.label}
                  type={field.name === 'password' ? (showPassword ? 'text' : 'password') : field.type}
                  {...register(field.name)}
                />
                <FormErrorMessage>{errors[field.name]?.message}</FormErrorMessage>
              </FormControl>
              {field.name === 'password' && (
                <Box width="full" textAlign="left" transform="translate(-10px,-20px)">
                  <Button
                    variant="ghost"
                    bg="transparent"
                    size="sm"
                    _hover={{ bg: 'transparent' }}
                    _active={{ bg: 'transparent' }}
                    onClick={() => {
                      setShowPassword(prev => !prev);
                    }}
                  >
                    {showPassword ? 'Hide password' : 'Show password'}
                  </Button>
                </Box>
              )}
            </Fragment>
          ))}
          {<input type="hidden" {...register('inviteCode', { value: inviteCode })} />}
          <AgreePoliciesCheckbox
            cookiesAccepted={cookiesAccepted}
            setCookiesAccepted={setCookiesAccepted}
            disabled={isSubmitting}
          />
          {create.error && (
            <Alert status="error" style={{ margin: '10px 0' }}>
              <AlertIcon />
              <AlertDescription>{create.error.message}</AlertDescription>
            </Alert>
          )}
          <SubmitButton
            mt={4}
            isLoading={isSubmitting}
            isDisabled={!cookiesAccepted}
            type="submit"
            style={{ backgroundColor: theme.colors.blue900, color: 'white' }}
          >
            Signup
          </SubmitButton>
        </Stack>
      </SpacingSignupContainer>
    </Flex>
  );
}

const SpacingSignupContainer = styled(SpacingContainer)`
  max-width: 400px;
  width: 100%;
`;

const HStackLoading = styled(Box)`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SubmitButton = styled(Button)`
  text-transform: capitalize;
  ${oldCss.greyBorder200};
`;
